import { useEffect, useRef } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"
import hash from "@emotion/hash"
import FocusTrap from "focus-trap-react"

import { useDictionary } from "contexts/Dictionary"
import { useConsole } from "contexts/Console"

import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"

import useScrollLock from "hooks/useScrollLock"

import Button from "components/button/Button"
import { headline40, body50, normal, light, headline50, bold, legend100 } from "css/text"

export const priceModal = `m${hash("price-modal")}`
export const weChatModal = `m${hash("we-chat-modal")}`
export const qrCodeModal = `m${hash("qr-code-modal")}`
export const a11yModal = `m${hash("a11y-modal")}`
export const langModal = `m${hash("lang-modal")}`
export const fallbackModal = `m${hash("fallback-modal")}`
export const downloadModal = `m${hash("download-modal")}`
export const specTerms = `m${hash("spec-terms")}`
export const shareModal = `m${hash("share-modal")}`
export const SAVModal = `m${hash("sav-modal")}`
export const moonphaseModal = `m${hash("moonphase-modal")}`

const Root = styled(motion.aside)`
  ${fullGrid}
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-top, 10000));
  background-color: rgb(var(--light-black) / 0.8);
`

const Container = styled(motion.div)`
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  overflow: auto;
  grid-column: doc;
  z-index: 1;

  ${getMediaQuery("m")} {
    .${a11yModal} &,
    .${weChatModal} &,
    .${priceModal} &,
    .${specTerms} &,
    .${downloadModal} &,
    .${qrCodeModal} &,
    .${fallbackModal} &,
    .${SAVModal} & {
      grid-column: col 2 / col -2;
    }
  }

  .${langModal} & {
    & div {
      border-radius: unset;
    }
  }

  ${getMediaQuery("l")} {
    /* .${langModal} &, */
    /* .${weChatModal} &, */
    .${specTerms} & {
      grid-column: col 3 / col -3;
    }
  }

  .${moonphaseModal} & {
    align-items: flex-start;
    block-size: var(--vh100, 100dvh);
    box-sizing: border-box;
    justify-content: center;
    padding-block: 20px;
    pointer-events: auto;
    touch-action: pan-y;
  }
`

const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: rgb(var(--pure-white));
  padding-inline: var(--grid-col-unit);
  will-change: transform;
  transform: translateY(0px);
  text-align: ${({ align }) => align || "initial"};
  max-height: 100vh;
  max-height: 100dvh;
  overflow: auto;

  pointer-events: none;

  /* content is centered with margin-block: auto (see Content): centered when it fits,
     starts at the top and scrolls when it doesn't. justify-content: center cut the top. */
  display: flex;
  flex-direction: column;

  .${langModal} & {
    height: 100%;
  }

  ${getMediaQuery("m")} {
    width: 100%;
    position: relative;
    inset: unset;
    border-radius: 50px;
    padding-inline: 6.25rem;
  }

  .${shareModal} & {
    background: none;
    height: 100%;
  }

  .${SAVModal} & {
    &[aria-labelledby="sav-ref-title"] > div {
      > p {
        &:last-of-type {
          margin-block-end: 3.12rem;
        }
      }
    }
  }

  .${moonphaseModal} & {
    background: none;
    border-radius: 0;
    inline-size: auto;
    inset: unset;
    margin-block: auto;
    max-block-size: none;
    overflow: visible;
    padding: 0;
    position: relative;
  }
`

export const Close = styled(Button)`
  --spacing: calc(var(--outer-margin) - var(--grid-gap) / 2 - var(--height) / 2);
  ${getMediaQuery("m")} {
    --spacing: var(--height);
  }
  pointer-events: all;
  position: absolute;
  top: var(--spacing);
  right: var(--spacing);
  [dir="rtl"] & {
    left: var(--spacing);
    right: auto;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;

  > svg {
    width: 16px;
    height: 16px;
  }

  .${shareModal} & {
    right: 1.25rem;
    top: 1.25rem;
  }
`

const Content = styled.div`
  margin-block: auto;
  overflow-y: auto;
  overflow: visible;
  padding-block: 6.25rem;
  scrollbar-width: none;
  pointer-events: all;

  & .fallbackLanguages {
    > h2 {
      ${headline50}
      margin-block-start: 2.5rem;
      color: rgb(var(--light-black));

      em {
        font-style: normal;
        color: rgb(var(--green));
        display: contents;
      }
    }
    > p {
      ${light}
      margin-block: 1.25rem  1.88rem;
      color: rgb(var(--light-black));

      ${getMediaQuery("m", { max: true })} {
        br {
          display: none;
        }
      }
    }

    ul {
      columns: 2;
      column-gap: var(--grid-gap);
      list-style: none;
      padding: 0;
      margin: 2rem 0 0 0;

      ${getMediaQuery("m", { min: true })} {
        columns: 3;
      }

      li {
        break-inside: avoid;
        white-space: nowrap;

        // Safari
        box-sizing: border-box;
        display: inline-block;
        width: 100%;
        margin-block-end: 0.675rem;
        border: none;
        padding: 0;

        a {
          ${normal}
          font-size: 1rem;
          cursor: pointer;
          align-items: center;
          color: rgb(var(--light-black));
          display: inline-flex;
          gap: 0.5rem;
          text-decoration: none;
          transition: color 0.3s;
          border: none;
          padding: 0;

          [dir="rtl"] &:not([dir="rtl"]) {
            flex-direction: row-reverse;
          }

          @media (hover: hover) {
            :hover {
              color: rgb(var(--green));
            }
          }

          .active & {
            color: rgb(var(--green));
            cursor: default;

            // bullet
            ::after {
              background-color: currentColor;
              border-radius: 50%;
              content: "";
              height: 0.5rem;
              width: 0.5rem;
              margin-block-start: 2px;
            }
          }
        }
      }
    }
  }

  .${langModal} &,
  .${SAVModal} & {
    overflow-y: auto;
  }

  .${moonphaseModal} & {
    overflow: visible;
    padding: 0;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  .${SAVModal} & {
    > h3 {
      ${headline40}
      margin-block-end: 1.25rem;
    }
    > p {
      ${body50}
    }
    > div {
      ${body50}
      > p:first-of-type {
        margin-block-end: 2.275rem;
      }
    }
    > picture {
      display: flex;
      justify-content: center;
      > img {
        width: 27rem;
        height: auto;

        ${getMediaQuery("m")} {
          height: 18.75rem;
          aspect-ratio: 36/25;
        }
      }
    }
  }
`

const Backerlay = styled.div`
  z-index: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 0;
`

export default function ModalContainer({
  onClose,
  className,
  modalHeading = "",
  modalContent = "",
  align,
  children,
  id,
  scrollTarget = "modal",
  showCloseButton = true,
}) {
  const console = useConsole()

  const dictionary = useDictionary()
  const scrollLock = useScrollLock()

  const containerRef = useRef()
  const modalRef = useRef()

  const onkeydown = e => {
    e.preventDefault()
    if (e.key === "Enter") onClose()
  }

  const onClickOutside = e => {
    e.stopPropagation()
    // If the click is outside the modal, close it
    const path = e.nativeEvent.composedPath ? e.nativeEvent.composedPath() : e.nativeEvent.path
    if (!path.includes(modalRef.current)) {
      onClose()
    }
  }

  const onClickCont = e => {
    if (scrollTarget === "container") {
      onClickOutside(e)
      return
    }

    e.stopPropagation()
  }

  useEffect(() => {
    const r = scrollTarget === "container" ? containerRef.current : modalRef.current
    scrollLock.lock(r)
    return () => scrollLock.unlock(r)
  }, [])

  function onDragEnd(e, info) {
    if (info.velocity.y > 100) onClose()
  }

  return (
    <FocusTrap focusTrapOptions={{ onDeactivate: onClose }}>
      <Root
        initial={{ opacity: 0, backdropFilter: "blur(0px) opacity(0)", WebkitBackdropFilter: "blur(0px) opacity(0)" }}
        animate={{ opacity: 1, backdropFilter: "blur(4px) opacity(1)", WebkitBackdropFilter: "blur(4px) opacity(1)" }}
        exit={{ opacity: 0, backdropFilter: "blur(0px) opacity(0)", WebkitBackdropFilter: "blur(0px) opacity(0)" }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={className}
      >
        <Backerlay onClickCapture={onClickOutside} />

        <Container
          ref={containerRef}
          onClick={onClickCont}
          // drag='y'
          // dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          // onDragEnd={onDragEnd}
          // dragElastic={{ bottom: 0.5 }}
          // dragTransition={{ bounceStiffness: 600, bounceDamping: 50 }}
        >
          <Modal
            initial={{ y: "20px" }}
            animate={{ y: "0px" }}
            exit={{ y: "-20px" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            ref={modalRef}
            role='dialog'
            aria-modal='true'
            aria-labelledby={modalHeading}
            {...(modalContent && { "aria-describedby": modalContent })} // aria-describedby may be unnecessary
            align={align}
            id={id}
          >
            <Content>{children}</Content>
            {showCloseButton && (
              <Close aria-label={dictionary.popinClose()} onPress={onClose} icon='close' className='icon translucent-light' onKeyDown={onkeydown}>
                <span>{dictionary.popinClose()}</span>
              </Close>
            )}
          </Modal>
        </Container>
      </Root>
    </FocusTrap>
  )
}
